import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Radio, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { collectPayment } from "@/lib/razorpay";
import { DARK_DIALOG, MfaField, fmtDate, errMsg } from "./shared";

const poll = async (tid, tries = 30) => {
  for (let i = 0; i < tries; i++) {
    const { data } = await api.get(`/admin/payment-gateway/live-test/${tid}`);
    if (data.status === "webhook_confirmed" || data.status === "failed") return data;
    await new Promise((r) => setTimeout(r, 3000));
  }
  return null;
};

export function MfaConfirm({ action, onClose }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    let next;
    try { next = await action.run(code); onClose(); } catch (e) { toast.error(errMsg(e) || e.message); } finally { setBusy(false); }
    if (typeof next === "function") next();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DARK_DIALOG} max-w-sm`} data-testid="pg-mfa-dialog">
        <DialogHeader><DialogTitle>{action.title}</DialogTitle><DialogDescription className="text-slate-400">Confirm with your authenticator code.</DialogDescription></DialogHeader>
        <MfaField value={code} onChange={setCode} testid="pg-mfa-input"/>
        <div className="flex justify-end"><Button className="bg-orange-500 hover:bg-orange-600" disabled={code.length !== 6 || busy} onClick={go} data-testid="pg-mfa-confirm">Confirm</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export function LiveChecksCard({ s, onChange, askMfa }) {
  const [busy, setBusy] = useState("");
  const lt = s.live_test;
  const ready = s.configured && s.environment === "live";
  useEffect(() => { setBusy(""); }, [s]);

  const selftest = async () => {
    setBusy("webhook");
    try { const { data } = await api.post("/admin/payment-gateway/webhook-selftest"); data.ok ? toast.success(data.detail) : toast.error(data.detail); }
    catch (e) { toast.error(errMsg(e)); }
    onChange();
  };
  const liveTest = () => askMfa({ title: "Start ₹1 live payment", run: async (code) => {
    const { data: order } = await api.post("/admin/payment-gateway/live-test/order", { mfa_code: code });
    // Open checkout only after the MFA dialog closes so it can receive clicks.
    return async () => {
      try {
        const paid = await collectPayment(order, { description: "Karmivo live payment check (₹1)" });
        setBusy("live");
        await api.post(`/admin/payment-gateway/live-test/${order.test_id}/verify`, paid);
        toast.message("Payment done. Waiting for Razorpay's webhook…");
        const res = await poll(order.test_id);
        res?.status === "webhook_confirmed" ? toast.success("Live payment confirmed by webhook") : toast.error("Webhook not received yet. Check the webhook URL, secret and events in Razorpay, then refresh.");
      } catch (e) { toast.error(errMsg(e) || e.message); }
      onChange();
    };
  } });
  const refund = () => askMfa({ title: "Refund the ₹1 test payment", run: async (code) => {
    await api.post(`/admin/payment-gateway/live-test/${lt.id}/refund`, { mfa_code: code });
    toast.success("Refund requested"); onChange();
  } });

  return (
    <div className="admin-card p-5 mt-5" data-testid="pg-live-checks-card">
      <div className="font-bold flex items-center gap-2"><Radio size={16} className="text-orange-400"/>Live checks</div>
      <p className="text-xs text-slate-500 mt-1 mb-4">Run these after the live keys are set and the webhook is added in Razorpay.</p>
      {(!ready || !s.webhook_secret_set) && <p className="text-xs text-amber-300 -mt-2 mb-4" data-testid="pg-live-checks-hint">Set live RAZORPAY_KEY_ID, RAZORPAY_KEY_SECRET and RAZORPAY_WEBHOOK_SECRET in server settings, then publish, to unlock these checks.</p>}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-800 p-4" data-testid="pg-webhook-selftest">
          <div className="text-sm font-semibold">Webhook address and signature</div>
          <div className="text-xs text-slate-500 mt-1">{s.webhook_selftest ? `${s.webhook_selftest.ok ? "Passed" : "Failed"} · ${fmtDate(s.webhook_selftest.at)}` : "Not run yet"}</div>
          {s.webhook_selftest && !s.webhook_selftest.ok && <div className="text-xs text-red-300 mt-1">{s.webhook_selftest.detail}</div>}
          <Button size="sm" className="mt-3 bg-orange-500 hover:bg-orange-600" disabled={!s.webhook_secret_set || !!busy} onClick={selftest} data-testid="pg-webhook-selftest-btn">
            {busy === "webhook" && <Loader2 size={14} className="animate-spin mr-1"/>}Run webhook check</Button>
        </div>
        <div className="rounded-lg border border-slate-800 p-4" data-testid="pg-live-test">
          <div className="text-sm font-semibold">₹1 live payment</div>
          <div className="text-xs text-slate-500 mt-1" data-testid="pg-live-test-status">
            {lt ? `${lt.status.replace("_", " ")} · ${fmtDate(lt.created_at)}${lt.refund_status ? ` · refund ${lt.refund_status}` : ""}` : "Not run yet"}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600" disabled={!ready || !s.webhook_secret_set || !!busy} onClick={liveTest} data-testid="pg-live-test-btn">
              {busy === "live" && <Loader2 size={14} className="animate-spin mr-1"/>}Pay ₹1 now</Button>
            {lt?.status === "webhook_confirmed" && !lt.refund_status &&
              <Button size="sm" variant="outline" className="border-slate-600 bg-transparent text-slate-300" onClick={refund} data-testid="pg-live-test-refund">Refund ₹1</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
